import { Circle, Flight } from '@mui/icons-material';
import { Grid, Step, Stepper, Typography, useTheme } from '@mui/material';
import { isISO8601Duration, toHourMinutes, toHrMinDuration, toWeekDayDateMonYear } from '~/modules/helpers';
import { Carrier, Leg } from '~/types';
import CarrierLogo from './CarrierLogo';
import { ReactNode } from 'react';
import useMediaQuery from '@mui/material/useMediaQuery';

export interface FlightCardItemLegProps {
  leg: Leg;
  carriers: Carrier[];
  legBottomContent: ReactNode;
  carrierLogoSize?: number;
  columngDistribution?: [number, number, number, number];
  showAirportFullName?: boolean;
  moreDetailsOnMobile?: boolean
}

function FlightCardItemLeg({ legBottomContent, leg, carriers, columngDistribution = [3, 3, 3, 3], carrierLogoSize = 40, showAirportFullName = false, moreDetailsOnMobile = false }: FlightCardItemLegProps) {
  const theme = useTheme();
  const isTab = useMediaQuery(theme.breakpoints.down('md'));
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Grid container>
      <Grid item xs={columngDistribution[0]}>
        <Typography variant='body2'>{toWeekDayDateMonYear(leg.departure.dateTime)}</Typography>
        <Typography variant='h6'>
          {(!isMobile || (isMobile && moreDetailsOnMobile))
            ? <>{leg.departure.city.friendlyName} {!showAirportFullName && <small>({leg.departure.location.code})</small>}</>
            : <>{leg.departure.location.code}</>
          }
        </Typography>
        {
          showAirportFullName &&
          <>
            <Typography variant="subtitle2">{leg.departure.location.friendlyName} <small>({leg.departure.location.code})</small></Typography>
            {
              leg.departure.terminal &&
              <Typography variant="subtitle2">Terminal: {leg.departure.terminal}</Typography>
            }

          </>
        }
        <Typography variant='h5'>{toHourMinutes(leg.departure.dateTime)}</Typography>
      </Grid>

      <Grid item xs={columngDistribution[1]}>
        <Typography variant='subtitle2' sx={{ textAlign: 'center' }}>{isISO8601Duration(leg.duration) ? toHrMinDuration(leg.duration) : leg.duration}</Typography>
        <Stepper>
          <Step><Circle sx={{ fontSize: 10 }} /></Step>
          <Step>
            <Flight sx={{ fontSize: 20, transform: 'rotate(90deg)', marginTop: .5 }} />
          </Step>
          <Step><Circle sx={{ fontSize: 10 }} /></Step>
        </Stepper>
        {legBottomContent}
      </Grid>

      <Grid item xs={columngDistribution[2]} pl={3}>
        <Typography variant='body2'>{toWeekDayDateMonYear(leg.arrival.dateTime)}</Typography>
        <Typography variant='h6'>
          {(!isTab || (isTab && moreDetailsOnMobile))
            ? <>{leg.arrival.city.friendlyName} {!showAirportFullName && <small>({leg.arrival.location.code})</small>}</>
            : <>{leg.arrival.location.code}</>
          }
        </Typography>
        {
          showAirportFullName &&
          <>
            <Typography variant="subtitle2">{leg.arrival.location.friendlyName} <small>({leg.arrival.location.code})</small></Typography>
            {
              leg.arrival.terminal &&
              <Typography variant="subtitle2">Terminal: {leg.arrival.terminal}</Typography>
            }
          </>
        }
        <Typography variant='h5'>{toHourMinutes(leg.arrival.dateTime)}</Typography>
      </Grid>

      <Grid
        item
        xs={columngDistribution[3]}
        container
        justifyContent="center"
        alignItems="center"
        position={isTab ? 'relative' : 'inherit'}
      >
        {carriers.map((carrier, i) => (
          <CarrierLogo
            boxStyle={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 5,
              width: carrierLogoSize,
              height: carrierLogoSize,
              borderRadius: "50%",
              border: '1px solid rgba(0, 0, 0, 0.12)',
              marginLeft: -10,
              background: '#fff',
              position: isTab ? 'absolute' : 'inherit',
              top: isTab ? '-18px' : 'inherit',
              right: isTab ? '-5px' : 'inherit',
            }}
            imageStyle={{
              height: 'auto',
              width: '80%',
            }}
            key={i}
            carrier={carrier}
            hideText
          />
        ))}
      </Grid>
    </Grid>
  );
}


export default FlightCardItemLeg;
